import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tvChannelService, TVChannelQueryParams } from '@/services/tvChannelService';
import { CreateTVChannelRequest } from '@/types/model';

export const tvChannelKeys = {
  all: ['tvChannels'] as const,
  lists: () => [...tvChannelKeys.all, 'list'] as const,
  list: (params: TVChannelQueryParams) => [...tvChannelKeys.lists(), params] as const,
  details: () => [...tvChannelKeys.all, 'detail'] as const,
  detail: (id: string) => [...tvChannelKeys.details(), id] as const,
};

export const useTVChannels = (params: TVChannelQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: tvChannelKeys.list(params),
    queryFn: () => tvChannelService.getTVChannels(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useTVChannel = (id: string) => {
  return useQuery({
    queryKey: tvChannelKeys.detail(id),
    queryFn: () => tvChannelService.getTVChannelById(id),
    enabled: !!id,
  });
};

export const useCreateTVChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tvChannelData: CreateTVChannelRequest) =>
      tvChannelService.createTVChannel(tvChannelData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.lists() });
    },
  });
};

export const useUpdateTVChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTVChannelRequest> }) =>
      tvChannelService.updateTVChannel(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.lists() });
    },
  });
};

export const useDeleteTVChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tvChannelService.deleteTVChannel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.lists() });
    },
  });
};

export const useToggleTVChannelStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      tvChannelService.toggleTVChannelStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: tvChannelKeys.lists() });
    },
  });
};

export const useTVChannelsForDropdown = () => {
  return useQuery({
    queryKey: ['tvChannels', 'all'],
    queryFn: () => tvChannelService.getTVChannels({ _page: 0, _size: 2000, active: true }),
    staleTime: 10 * 60 * 1000,
    select: (data) => data.content,
  });
};
